import React, { useState } from 'react';
import { Users, MessageCircle, Gift, ChevronRight, Calendar } from 'lucide-react';
import { RelationshipData } from '../hooks/useStatsData';
import { CardSkeleton } from './CardSkeleton';
interface CardRelationshipSnapshotProps {
  loading: boolean;
  data: RelationshipData | null;
}
export const CardRelationshipSnapshot: React.FC<CardRelationshipSnapshotProps> = ({
  loading,
  data
}) => {
  const [selectedAngelId, setSelectedAngelId] = useState<string | null>(null);
  if (loading) return <CardSkeleton data-id="element-20883" />;
  if (!data || data.top.length === 0) return <div className="bg-white dark:bg-gradient-to-b dark:from-[#0f0c29] dark:to-[#302b63] rounded-lg border border-purple-200/50 dark:border-indigo-500/30 p-5 shadow-sm dark:shadow-[0_0_15px_rgba(138,43,226,0.2)] backdrop-blur-sm" data-id="element-20884">
        <div className="flex items-center justify-between mb-4" data-id="element-20885">
          <h3 className="font-semibold text-lg flex items-center gap-2 text-gray-800 dark:text-transparent dark:bg-clip-text dark:bg-gradient-to-r dark:from-blue-300 dark:to-cyan-200" data-id="element-20886">
            <Users className="text-blue-500 dark:text-blue-400" size={18} data-id="element-20887" />
            Relationship Snapshot
          </h3>
        </div>
        <div className="flex flex-col items-center justify-center py-8" data-id="element-20888">
          <p className="text-gray-500 dark:text-indigo-200/70 text-sm" data-id="element-20889">
            Form connections with Angels to build relationships.
          </p>
          <button className="mt-5 px-4 py-2 bg-gradient-to-r from-blue-500 to-indigo-500 dark:from-blue-600 dark:to-indigo-600 text-white rounded-full text-sm font-medium border border-blue-300/30 dark:border-blue-500/30 shadow-sm dark:shadow-[0_0_10px_rgba(59,130,246,0.2)]" onClick={() => {
        // Navigate to discovery feed
        const event = new CustomEvent('navigate', {
          detail: {
            section: 'discovery_feed'
          }
        });
        document.dispatchEvent(event);
      }} data-id="element-20890">
            Discover Angels
          </button>
        </div>
      </div>;
  // Set default selected angel if none is selected
  if (!selectedAngelId && data.top.length > 0) {
    setSelectedAngelId(data.top[0].id);
  }
  const selectedAngel = data.top.find(angel => angel.id === selectedAngelId);
  const angelDetails = selectedAngelId ? data.details[selectedAngelId] : null;
  return <div className="bg-white dark:bg-gradient-to-b dark:from-[#0f0c29] dark:to-[#302b63] rounded-lg border border-purple-200/50 dark:border-indigo-500/30 p-5 shadow-sm dark:shadow-[0_0_15px_rgba(138,43,226,0.2)] backdrop-blur-sm relative overflow-hidden" data-id="element-20891">
      <div className="relative" data-id="element-20892">
        <div className="flex items-center justify-between mb-4" data-id="element-20893">
          <h3 className="font-semibold text-lg flex items-center gap-2 text-gray-800 dark:text-transparent dark:bg-clip-text dark:bg-gradient-to-r dark:from-blue-300 dark:to-cyan-200" data-id="element-20894">
            <Users className="text-blue-500 dark:text-blue-400" size={18} data-id="element-20895" />
            Relationship Snapshot
          </h3>
        </div>
        {/* Angel selector */}
        <div className="flex gap-2 mb-4 overflow-x-auto pb-2 scrollbar-hide" data-id="element-20896">
          {data.top.map(angel => <button key={angel.id} className={`flex-shrink-0 flex items-center gap-2 px-3 py-1.5 rounded-full text-sm border ${selectedAngelId === angel.id ? 'bg-gradient-to-r from-blue-500 to-indigo-500 dark:from-blue-600 dark:to-indigo-600 text-white border-blue-300/30 dark:border-blue-500/30 shadow-sm dark:shadow-[0_0_10px_rgba(59,130,246,0.2)]' : 'bg-blue-50 dark:bg-blue-900/30 backdrop-blur-sm text-blue-500 dark:text-blue-300 border-blue-200/50 dark:border-blue-500/20'}`} onClick={() => setSelectedAngelId(angel.id)} data-id="element-20897">
              <div className="relative" data-id="element-20898">
                <img src={angel.avatar} alt={angel.name} className="w-5 h-5 rounded-full object-cover border border-blue-200/50 dark:border-blue-400/30" data-id="element-20899" />
                {selectedAngelId === angel.id && <div className="absolute inset-0 w-5 h-5 rounded-full border border-blue-400 animate-pulse-slow opacity-70" data-id="element-20900"></div>}
              </div>
              <span data-id="element-20901">{angel.name}</span>
            </button>)}
        </div>
        {selectedAngel && angelDetails && <>
            <div className="grid grid-cols-3 gap-2 mb-4" data-id="element-20902">
              <div className="bg-blue-50 dark:bg-blue-900/30 backdrop-blur-sm rounded-lg p-3 text-center border border-blue-200/50 dark:border-blue-500/20 shadow-sm dark:shadow-[0_0_10px_rgba(59,130,246,0.1)]" data-id="element-20903">
                <div className="text-blue-500 dark:text-blue-300 flex items-center justify-center gap-1.5 mb-1" data-id="element-20904">
                  <Calendar size={14} data-id="element-20905" />
                  <span className="text-xs" data-id="element-20906">Connection Age</span>
                </div>
                <p className="font-medium text-blue-600 dark:text-blue-200 etched-text" data-id="element-20907">
                  {angelDetails.ageDays} days
                </p>
              </div>
              <div className="bg-purple-50 dark:bg-purple-900/30 backdrop-blur-sm rounded-lg p-3 text-center border border-purple-200/50 dark:border-purple-500/20 shadow-sm dark:shadow-[0_0_10px_rgba(168,85,247,0.1)]" data-id="element-20908">
                <div className="text-purple-500 dark:text-purple-300 flex items-center justify-center gap-1.5 mb-1" data-id="element-20909">
                  <MessageCircle size={14} data-id="element-20910" />
                  <span className="text-xs" data-id="element-20911">Messages</span>
                </div>
                <p className="font-medium text-purple-600 dark:text-purple-200 etched-text" data-id="element-20912">
                  {angelDetails.messagesCount}
                </p>
              </div>
              <div className="bg-pink-50 dark:bg-pink-900/30 backdrop-blur-sm rounded-lg p-3 text-center border border-pink-200/50 dark:border-pink-500/20 shadow-sm dark:shadow-[0_0_10px_rgba(236,72,153,0.1)]" data-id="element-20913">
                <div className="text-pink-500 dark:text-pink-300 flex items-center justify-center gap-1.5 mb-1" data-id="element-20914">
                  <Gift size={14} data-id="element-20915" />
                  <span className="text-xs" data-id="element-20916">Gifts</span>
                </div>
                <p className="font-medium text-pink-600 dark:text-pink-200 etched-text" data-id="element-20917">
                  {angelDetails.giftsSent}
                </p>
              </div>
            </div>
            {/* Milestones */}
            <div className="mb-4" data-id="element-20918">
              <h4 className="text-sm font-medium mb-2 text-gray-700 dark:text-indigo-200" data-id="element-20919">
                Relationship Milestones
              </h4>
              <div className="space-y-2" data-id="element-20920">
                {angelDetails.milestones.map(milestone => <div key={milestone.id} className={`flex items-center justify-between p-2 rounded-lg border ${milestone.unlocked ? 'bg-green-50 dark:bg-green-900/30 backdrop-blur-sm text-green-600 dark:text-green-300 border-green-200/50 dark:border-green-500/20 shadow-sm dark:shadow-[0_0_10px_rgba(16,185,129,0.1)]' : 'bg-gray-50 dark:bg-gray-900/30 backdrop-blur-sm text-gray-500 dark:text-gray-400 border-gray-200/50 dark:border-gray-500/20'}`} data-id="element-20921">
                    <span className="text-sm" data-id="element-20922">{milestone.name}</span>
                    {!milestone.unlocked && milestone.condition && <span className="text-xs" data-id="element-20923">{milestone.condition}</span>}
                  </div>)}
              </div>
            </div>
            {/* AI Insights */}
            {angelDetails.insights.length > 0 && <div className="mb-4" data-id="element-20924">
                <h4 className="text-sm font-medium mb-2 text-gray-700 dark:text-indigo-200" data-id="element-20925">
                  AI Insights
                </h4>
                <div className="bg-indigo-50 dark:bg-indigo-900/30 backdrop-blur-sm rounded-lg p-4 border border-indigo-200/50 dark:border-indigo-500/20 shadow-sm dark:shadow-[0_0_10px_rgba(79,70,229,0.1)]" data-id="element-20926">
                  <ul className="text-sm text-indigo-600 dark:text-indigo-300 space-y-1" data-id="element-20927">
                    {angelDetails.insights.map((insight, index) => <li key={index} className="flex items-start gap-2" data-id="element-20928">
                        <span className="text-indigo-500 dark:text-indigo-400" data-id="element-20929">
                          •
                        </span>
                        <span data-id="element-20930">{insight}</span>
                      </li>)}
                  </ul>
                </div>
              </div>}
            <div className="mt-4 pt-3 border-t border-purple-200/30 dark:border-indigo-500/20" data-id="element-20931">
              <button className="w-full py-2 bg-gradient-to-r from-blue-500 to-indigo-500 dark:from-blue-600 dark:to-indigo-600 text-white rounded-lg text-sm font-medium hover:shadow-lg transition-all flex items-center justify-center gap-1 border border-blue-300/30 dark:border-blue-500/30 shadow-sm dark:shadow-[0_0_10px_rgba(59,130,246,0.2)]" onClick={() => {
            // Navigate to relationship tracking
            const event = new CustomEvent('navigate', {
              detail: {
                section: 'relationship_tracking'
              }
            });
            document.dispatchEvent(event);
          }} data-id="element-20932">
                <span data-id="element-20933">View Relationship Details</span>
                <ChevronRight size={16} data-id="element-20934" />
              </button>
            </div>
          </>}
      </div>
    </div>;
};